import { createSelector } from 'reselect';

import { getBookGameId } from 'root/lib/utilityFunctions';

import { FIRST_CHAPTER_ID_REF, TITLE_ID_REF, COVER_ID_REF } from '../../data/constants';
import READING_MODES from '../../data/reading_modes';
import { GRAPHIC_ORGANIZER, POST_READING } from '../../data/activity_types';

export const getSelectedActivity = ({ selectedActivity }) => selectedActivity;

export const getIsQuestWorkflow = createSelector([getSelectedActivity], ({ isQuestWorkflow }) => isQuestWorkflow);

export const getRoundSessionId = ({ appSessionReducer: { searchlight: { roundSessionId } } }) => roundSessionId;

export const getSavedRoundSessionId = ({ eReader: { bookInfo: { roundSessionId } } }) => roundSessionId;

export const getBookId = ({
  eReader: {
    bookInfo: { bookId },
  },
}) => bookId;

export const getChapterId = ({
  eReader: {
    bookInfo: { currentChapterId, chapters },
  },
}) => {
  const chapter = (chapters || []).find(({ spineIdRef }) => spineIdRef === currentChapterId) || {};

  return chapter.index || 0;
};

export const getChapterNumber = ({
  eReader: {
    bookInfo: { currentChapterId, chapters },
  },
}) => {
  const formattedChapterId = [TITLE_ID_REF, COVER_ID_REF].includes(currentChapterId)
    ? FIRST_CHAPTER_ID_REF
    : currentChapterId;
  const chapter = (chapters || []).find(({ spineIdRef }) => spineIdRef === formattedChapterId) || {};

  return chapter.index || 0;
};

export const getBookLevel = createSelector(getChapterNumber, (chapterNumber) => chapterNumber || 1);

/**
 * Resolves the skill id reported for the current book.
 *
 * Prefers the `skillId` handed down by the source that launched the eReader (e.g. a quest activity).
 * When it is missing, falls back to deriving one from the book and the current chapter number
 * (`ereader.<bookId>-<chapterNumber>`). Returns `undefined` when neither is available — a book with
 * no chapters (non-chapter book) has no chapter number to derive from.
 *
 * @returns {string|undefined} The skill id, or `undefined` if it is neither provided nor derivable.
 */
export const getBookSkillId = createSelector(
  [getSelectedActivity, getChapterNumber, getBookId],
  (selectedActivity, chapterNumber, bookId) => {
    const { skillId } = selectedActivity.params || {};
    const gameId = getBookGameId(bookId);

    if (skillId) {
      return skillId;
    }

    if (gameId && chapterNumber) {
      return `${gameId}-${chapterNumber}`;
    }

    return undefined;
  },
);

export const getReadingMode = ({
  eReader: {
    readingMode: { currentMode },
  },
}) => (currentMode === READING_MODES.CLEAN ? 'clean_reading' : 'activity_reading');

export const getActivityIndex = (
  { eReader: { inlineActivities: { all, order = [] } } },
  { activityType, questionId },
) => (
  activityType === GRAPHIC_ORGANIZER || activityType === POST_READING
    ? Object.keys(all).length
    : order.findIndex(id => id === questionId.toString())
);

export const getQuestionCount = ({
  eReader: {
    accurateResponseData: { totalQuestions },
  },
}) => totalQuestions;

export const getQuestionsCorrect = ({
  eReader: {
    accurateResponseData: { totalCorrect },
  },
}) => totalCorrect;

export const getSettings = ({
  eReader: {
    userSettings: { fontSize, showSentenceNumbers, showReadForMe, showRevealWords },
  },
}) => ({
  fontSize,
  showSentenceNumbers,
  showReadForMe,
  showRevealWords,
});
